import { useMemo, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  civilToday,
  exportQueryOf,
  formatCivilDate,
  links,
  resolveFormattingLocale,
  useListSearch,
} from '@eis/core';
import type { BillingApi } from '@eis/billingservice-sdk';
import {
  ExportButton,
  InfiniteDataGrid,
  PropertyFilter,
  PageHeader,
  PageLayout,
  RequirePermission,
  StatusChip,
  cardSurfaceSx,
  useGridExportColumns,
  type ExportableGridColDef,
} from '@eis/patterns';
import { billingPermissions as p } from '../../../permissions';
import {
  isBillingUnavailable,
  useFilterCatalog,
  useInfiniteList,
  useRequestBillRun,
  type BillRunKind,
} from '../api/runs';
import { RUN_KINDS, toBillRunRows, type BillRunRow } from '../model/types';
import { RefusalAlert } from '../../invoices/ui/BillingDialogs';

/** The run catalog's sortable keys, by column. */
const SORT_KEYS: Readonly<Record<string, string>> = {
  code: 'code',
  kind: 'kind',
  runDate: 'runDate',
  outcome: 'outcome',
  startedAt: 'startedAt',
};

/**
 * Rechnungsläufe — the runs over the whole book (SDD-015 §5.6, §7.1): kind, date, what was
 * selected, created, skipped and failed, and the outcome. "Lauf starten" (`bill-runs.run`) asks
 * the service for a run of one kind for one date and shows what it did; the report drawer shows a
 * run's refusals, so a PARTIAL or FAILED run names the policies it could not bill.
 *
 * The list is the standard one (SDD-017 §9): the property filter over the run catalog (kind,
 * date, outcome, start), server order — newest first unless the reader orders otherwise — and one
 * scroll, all in the URL. A run has nothing to SEARCH (the service answers `search` with
 * INVALID_SEARCH / NOT_ALLOWED), so the filter offers no free-text token here.
 */
export function BillRunsPage(): ReactNode {
  const { t, i18n } = useTranslation('billing');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  const url = useListSearch();
  const [starting, setStarting] = useState(false);
  const [report, setReport] = useState<BillRunRow | null>(null);
  const catalog = useFilterCatalog('billRuns');
  const runs = useInfiniteList('billRuns', {
    ...(url.params.filters === undefined ? {} : { filters: url.params.filters }),
    ...(url.params.order === undefined ? {} : { order: url.params.order }),
  });
  const rows = useMemo(() => toBillRunRows(runs.items), [runs.items]);

  // Each column names its field of `BillRun` (SDD-020 §8.1).
  const columns = useMemo<ExportableGridColDef<BillRunRow>[]>(
    () => [
      {
        field: 'code',
        headerName: t('runs.columns.code'),
        exportPath: 'code',
        width: 170,
        renderCell: ({ row }) => <span style={{ fontFamily: 'monospace' }}>{row.code}</span>,
      },
      {
        field: 'kind',
        headerName: t('runs.columns.kind'),
        exportPath: 'kind',
        width: 140,
        valueFormatter: (value: string) =>
          t(`billRuns.kind.${value}` as never, { defaultValue: value }),
      },
      {
        field: 'runDate',
        headerName: t('runs.columns.runDate'),
        exportPath: 'runDate',
        exportType: 'DATE',
        width: 130,
        valueFormatter: (value: string) => (value === '' ? '—' : formatCivilDate(value, locale)),
      },
      {
        field: 'selected',
        headerName: t('runs.columns.selected'),
        exportPath: 'selected',
        width: 110,
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'created',
        headerName: t('runs.columns.created'),
        exportPath: 'created',
        width: 110,
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'skipped',
        headerName: t('runs.columns.skipped'),
        exportPath: 'skipped',
        width: 130,
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'failed',
        headerName: t('runs.columns.failed'),
        exportPath: 'failed',
        width: 130,
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'outcome',
        headerName: t('runs.columns.outcome'),
        exportPath: 'outcome',
        width: 150,
        renderCell: ({ row }) => (
          <StatusChip
            status={row.outcome}
            label={t(`billRuns.outcome.${row.outcome}` as never, { defaultValue: row.outcome })}
          />
        ),
      },
      {
        field: 'startedAt',
        headerName: t('runs.columns.startedAt'),
        flex: 1,
        minWidth: 200,
        exportPath: 'startedAt',
      },
    ],
    [t, locale],
  );
  const exportColumns = useGridExportColumns(columns);

  const header = (
    <PageHeader
      title={t('runs.title')}
      description={t('runs.subtitle')}
      actions={
        <Stack direction="row" spacing={1}>
          <ExportButton
            service="billing"
            permission={p.exports.create}
            rowCount={runs.isLoading ? undefined : rows.length}
            buildRequest={(format) => ({
              collection: 'bill-runs',
              query: exportQueryOf({ filters: url.params.filters, order: url.params.order }),
              columns: exportColumns,
              format,
              label: t('runs.title'),
            })}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              void navigate({ href: links.billing.invoices() });
            }}
          >
            {t('runs.actions.invoices')}
          </Button>
          <RequirePermission anyOf={[p.billRuns.run]}>
            <Button
              variant="contained"
              size="small"
              startIcon={<PlayArrowOutlinedIcon />}
              onClick={() => {
                setStarting(true);
              }}
            >
              {t('runs.actions.run')}
            </Button>
          </RequirePermission>
        </Stack>
      }
    />
  );

  return (
    <PageLayout header={header}>
      {/* The list in its table card (design v2, prompt 24). */}
      <Paper variant="outlined" sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}>
        <Stack spacing={2}>
          <PropertyFilter
            catalog={catalog.data}
            value={{ filters: url.params.filters ?? '', search: '' }}
            onChange={(next) => {
              url.set({ filters: next.filters });
            }}
            allowSearch={false}
          />
          <InfiniteDataGrid
            rows={rows}
            list={runs}
            columns={columns}
            ariaLabel={t('runs.ariaLabel')}
            order={url.params.order}
            onOrderChange={(order) => {
              url.set({ order });
            }}
            sortKeys={SORT_KEYS}
            errorDescription={
              isBillingUnavailable(runs.error) ? t('runs.error.unavailable') : t('runs.error.list')
            }
            emptyTitle={t('runs.empty.title')}
            emptyDescription={t('runs.empty.description')}
            onRowClick={(row) => {
              setReport(row);
            }}
          />
        </Stack>

        <RunNowDialog
          open={starting}
          onClose={() => {
            setStarting(false);
          }}
        />

        <Drawer
          anchor="right"
          open={report !== null}
          onClose={() => {
            setReport(null);
          }}
          // The drawer is a dialog to assistive technology; its name is the report's heading.
          slotProps={{ paper: { 'aria-labelledby': 'run-report-title' } }}
        >
          {report !== null && (
            <Box sx={{ width: 480, maxWidth: '100vw', p: 3 }}>
              <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h3" component="h2" id="run-report-title">
                  {t('runs.report.title', { code: report.code })}
                </Typography>
                <IconButton
                  aria-label={t('runs.report.close')}
                  onClick={() => {
                    setReport(null);
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                <StatusChip
                  status={report.outcome}
                  label={t(`billRuns.outcome.${report.outcome}` as never, {
                    defaultValue: report.outcome,
                  })}
                />
                <Typography variant="body2">
                  {t('runs.report.counts', {
                    selected: report.selected,
                    created: report.created,
                    skipped: report.skipped,
                    failed: report.failed,
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('runs.report.triggeredBy')}:{' '}
                  {report.triggeredBy === '' ? '—' : report.triggeredBy} ·{' '}
                  {t('runs.report.finishedAt')}:{' '}
                  {report.finishedAt === '' ? '—' : report.finishedAt}
                </Typography>
                {Object.keys(report.report).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('runs.report.empty')}
                  </Typography>
                ) : (
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                      overflow: 'auto',
                      fontSize: 'inherit',
                    }}
                    data-testid="run-report"
                  >
                    {JSON.stringify(report.report, null, 2)}
                  </Box>
                )}
              </Stack>
            </Box>
          )}
        </Drawer>
      </Paper>
    </PageLayout>
  );
}

function RunNowDialog({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('billing');
  const request = useRequestBillRun();
  const [kind, setKind] = useState<BillRunKind>('INVOICE');
  const [runDate, setRunDate] = useState(() => civilToday());
  const [result, setResult] = useState<BillingApi.BillRun | null>(null);

  const close = (): void => {
    setResult(null);
    request.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{t('runs.dialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('runs.dialog.description')}
          </Typography>
          <TextField
            select
            size="small"
            label={t('runs.dialog.kind')}
            value={kind}
            onChange={(event) => {
              setKind(event.target.value as BillRunKind);
            }}
          >
            {RUN_KINDS.map((candidate) => (
              <MenuItem key={candidate} value={candidate}>
                {t(`billRuns.kind.${candidate}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            type="date"
            required
            label={t('runs.dialog.runDate')}
            value={runDate}
            onChange={(event) => {
              setRunDate(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <RefusalAlert error={request.error} />
          {result !== null && (
            <Alert severity="success" data-testid="run-started">
              {t('runs.dialog.started', {
                code: result.code ?? '',
                created: result.created ?? 0,
                skipped: result.skipped ?? 0,
                failed: result.failed ?? 0,
              })}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>
          {result === null ? t('dialogs.cancel') : t('dialogs.close')}
        </Button>
        <Button
          variant="contained"
          disabled={request.isPending || result !== null || !/^\d{4}-\d{2}-\d{2}$/.test(runDate)}
          onClick={() => {
            void request
              .mutateAsync({ kind, runDate })
              .then((response) => {
                setResult(response.billRun ?? null);
              })
              .catch(() => undefined);
          }}
        >
          {t('runs.dialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
